import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useBooking } from "@/context/BookingContext";
import { CONDITIONS } from "@/data/content";
import { ShieldCheck, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", phone: "", email: "", condition: "", preferred_date: "", location: "", message: "" };
const inputCls = "bg-white border-slate-300 text-[#0b2c4d] placeholder:text-slate-400 focus-visible:ring-brand";

const ContactFields = ({ form, update }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="ap-name" className="text-slate-700">Full Name *</Label>
      <Input
        id="ap-name"
        data-testid="appointment-name-input"
        value={form.name}
        onChange={(e) => update("name", e.target.value)}
        placeholder="Your name"
        className={inputCls}
      />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="ap-phone" className="text-slate-700">Phone *</Label>
        <Input
          id="ap-phone"
          data-testid="appointment-phone-input"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="+91 …"
          className={inputCls}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ap-email" className="text-slate-700">Email</Label>
        <Input
          id="ap-email"
          type="email"
          data-testid="appointment-email-input"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="Optional"
          className={inputCls}
        />
      </div>
    </div>
  </>
);

const ConcernDateFields = ({ form, update }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="space-y-2">
      <Label className="text-slate-700">Primary Concern</Label>
      <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
        <SelectTrigger
          data-testid="appointment-condition-select"
          className="bg-white border-slate-300 text-[#0b2c4d] focus:ring-brand"
        >
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200 text-[#0b2c4d]">
          {CONDITIONS.map((c) => (
            <SelectItem key={c.title} value={c.title} className="focus:bg-brand-light focus:text-brand">
              {c.title}
            </SelectItem>
          ))}
          <SelectItem value="Other / Not sure" className="focus:bg-brand-light focus:text-brand">
            Other / Not sure
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label htmlFor="ap-date" className="text-slate-700">Preferred Date</Label>
      <Input
        id="ap-date"
        type="date"
        data-testid="appointment-date-input"
        value={form.preferred_date}
        onChange={(e) => update("preferred_date", e.target.value)}
        className="bg-white border-slate-300 text-[#0b2c4d] focus-visible:ring-brand"
      />
    </div>
  </div>
);

const LocationField = ({ value, onChange }) => (
  <div className="space-y-2">
    <Label className="text-slate-700">Preferred Location</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid="appointment-location-select"
        className="bg-white border-slate-300 text-[#0b2c4d] focus:ring-brand"
      >
        <SelectValue placeholder="Select a clinic" />
      </SelectTrigger>
      <SelectContent className="bg-white border-slate-200 text-[#0b2c4d]">
        <SelectItem value="CARE Hospitals, Gachibowli (Day: Mon–Sat, 10 AM–5 PM)" className="focus:bg-brand-light focus:text-brand">
          CARE Hospitals, Gachibowli — Day (Mon–Sat, 10 AM–5 PM)
        </SelectItem>
        <SelectItem value="Indi Spine-Brain Neuro Care, Madinaguda (Evening: Mon–Sat, 5:30–9 PM)" className="focus:bg-brand-light focus:text-brand">
          Madinaguda Clinic — Evening (Mon–Sat, 5:30–9 PM)
        </SelectItem>
        <SelectItem value="Either / Not sure" className="focus:bg-brand-light focus:text-brand">
          Either / Not sure
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const validateBookingForm = (form) => Boolean(form.name.trim() && form.phone.trim());

// Returns true if the pre-opened tab was successfully redirected to WhatsApp
const redirectToWhatsApp = (waWindow, url) => {
  if (waWindow && !waWindow.closed) {
    waWindow.location.href = url;
    return true;
  }
  return false;
};

const notifyBookingSuccess = (data, opened) => {
  if (!data.whatsapp_url) {
    toast.success(data.message || "Request received.");
    return;
  }
  if (opened) {
    toast.success(data.message || "Request received. Continuing on WhatsApp…");
    return;
  }
  // Popup blocked — give the user a visible way to continue
  toast.success("Request received. Tap below to continue on WhatsApp.", {
    duration: 20000,
    action: { label: "Open WhatsApp", onClick: () => { window.location.href = data.whatsapp_url; } },
  });
};

const BookingForm = ({ form, update, isMri, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-2">
    <ContactFields form={form} update={update} />
    <ConcernDateFields form={form} update={update} />
    <LocationField value={form.location} onChange={(v) => update("location", v)} />

    <div className="space-y-2">
      <Label htmlFor="ap-msg" className="text-slate-700">
        {isMri ? "Describe your symptoms" : "Message"}
      </Label>
      <Textarea
        id="ap-msg"
        data-testid="appointment-message-input"
        value={form.message}
        onChange={(e) => update("message", e.target.value)}
        placeholder={isMri ? "Brief history & symptoms…" : "Anything you'd like us to know…"}
        rows={3}
        className={`${inputCls} resize-none`}
      />
    </div>

    <div className="flex items-start gap-2 text-xs text-slate-500">
      <ShieldCheck className="w-4 h-4 text-brand shrink-0 mt-0.5" />
      <span>Your information is kept confidential and used only to support your care.</span>
    </div>

    <Button
      type="submit"
      data-testid="appointment-submit-btn"
      disabled={loading}
      className="w-full bg-brand hover:bg-brand-hover text-white font-semibold h-12 rounded-xl group"
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {isMri ? "Continue on WhatsApp" : "Request Appointment"}
          <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
        </>
      )}
    </Button>
  </form>
);

const AppointmentModal = () => {
  const { open, setOpen, type } = useBooking();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const isMri = type === "mri_review";
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBookingForm(form)) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    // Pre-open a tab synchronously (within user activation) so popup blockers allow it
    const waWindow = window.open("", "_blank");
    try {
      const { data } = await axios.post(`${API}/appointments`, {
        ...form,
        request_type: isMri ? "mri_review" : "appointment",
      });
      setForm(emptyForm);
      setOpen(false);
      const opened = data.whatsapp_url ? redirectToWhatsApp(waWindow, data.whatsapp_url) : false;
      if (!opened && waWindow && !waWindow.closed) waWindow.close();
      notifyBookingSuccess(data, opened);
    } catch (err) {
      if (waWindow) waWindow.close();
      toast.error("Something went wrong. Please call +91 88860 38800.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="appointment-modal"
        className="sm:max-w-[520px] bg-white border-slate-200 text-[#0b2c4d] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight text-[#0b2c4d]">
            {isMri ? "Send MRI for Review" : "Book an Appointment"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {isMri
              ? "Share your details and our team will guide you to securely send your MRI on WhatsApp."
              : "Consult at CARE Hospitals, Gachibowli (Mon–Sat, 10 AM–5 PM) or the Madinaguda evening clinic (Mon–Sat, 5:30–9 PM)."}
          </DialogDescription>
        </DialogHeader>

        <BookingForm form={form} update={update} isMri={isMri} loading={loading} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentModal;
